<template>
  <f7-page>
    <f7-navbar :title="$t('group.createCustomerCommunity')" :back-link/>
    <AppLayout>
      <template #main>
        
    <f7-block v-if="loading"><f7-preloader /></f7-block>
    <f7-block v-else-if="eligibility && !eligibility.eligible" strong inset class="eligibility-banner">
      <p>{{ eligibility.reason }}</p>
    </f7-block>
    <f7-block><f7-progressbar :progress="progress" /><p>{{ stepLabel }}</p></f7-block>

    <f7-list v-if="step === 1" form inset strong media-list outline dividers>
      <f7-list-input v-model:value="form.communityName" :label="$t('shopCommunity.communityName')" required />
      <f7-list-input v-model:value="form.description" :label="$t('common.description')" type="textarea" />
      <f7-button fill round raised @click="step++">{{ $t('group.creationNextStep') }}</f7-button>
    </f7-list>

    <f7-list v-if="step === 2" form inset strong media-list outline dividers>
      <f7-list-input :lable="$t('group.visibility')" type="select"  v-model:value="form.accessMode">
            <option value="INVITE_ONLY" selected>{{ $t('group.inviteOnly') }}</option>
            <option value="SHOP_CUSTOMERS_ONLY">{{ $t('group.shopCustomersOnly') }}</option>
            <option value="PUBLIC">{{ $t('common.public') }}</option>
            <option value="UNLISTED">{{ $t('group.unlisted') }}</option>        
      </f7-list-input>
      <f7-list-item :title="$t('shopCommunity.requireJoinApproval')"><template #after><f7-toggle v-model="form.requireJoinApproval" /></template></f7-list-item>
      <f7-list-item :title="$t('shopCommunity.membersCanInvite')"><template #after><f7-toggle v-model="form.membersCanInvite" /></template></f7-list-item>
      <f7-button fill round raised @click="step++">{{ $t('group.creationNextStep') }}</f7-button>
    </f7-list>

    <f7-list v-if="step === 3" form inset strong media-list outline dividers>
      <f7-list-item :title="$t('shopCommunity.customerChat')"><template #after><f7-toggle v-model="form.chatEnabled" /></template></f7-list-item>
      <f7-list-item :title="$t('shopCommunity.productAnnouncements')"><template #after><f7-toggle v-model="form.productAnnouncementsEnabled" /></template></f7-list-item>
      <f7-list-item :title="$t('shopCommunity.privateCampaigns')"><template #after><f7-toggle v-model="form.campaignsEnabled" /></template></f7-list-item>
      <f7-list-item :title="$t('shopCommunity.calendarEvents')"><template #after><f7-toggle v-model="form.calendarEnabled" /></template></f7-list-item>
      <f7-list-item :title="$t('common.documents')"><template #after><f7-toggle v-model="form.documentsEnabled" /></template></f7-list-item>
      <f7-button fill round raised @click="step++">{{ $t('group.creationNextStep') }}</f7-button>
    </f7-list>

    <f7-list v-if="step === 4" form inset strong media-list outline dividers>
      <f7-list-item :title="$t('shopCommunity.inviteExisting')"><template #after><f7-toggle v-model="form.inviteExistingCustomers" /></template></f7-list-item>
      <f7-list-item :title="$t('shopCommunity.autoInviteFuture')"><template #after><f7-toggle v-model="form.autoInviteFutureCustomers" /></template></f7-list-item>
      <f7-list-item :title="$t('shopCommunity.marketingOptIn')"><template #after><f7-toggle v-model="form.marketingOptInRequired" /></template></f7-list-item>
      <f7-button fill round raised @click="step++">{{ $t('common.review') || 'Review' }}</f7-button>
    </f7-list>

    <f7-block v-if="step === 5" strong inset outline>
      <h2>{{ $t('shopCommunity.readyToLaunch') }}</h2>
      <p>{{ $t('shopCommunity.launchDesc', { name: form.communityName }) }}</p>
      <f7-button fill round raised large @click="submitCommunity">{{ $t('shopCommunity.createCommunity') }}</f7-button>
    </f7-block>
    
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { f7 } from 'framework7-vue'
import { useMarketplaceShop } from '@/js/composables/useMarketplaceShop'
import AppLayout from '../components/common/AppLayout.vue'
import i18n from '@/js/i18n'

const props = defineProps({ shopId: { type: String, required: true }, f7route: Object, f7router: Object })
const { eligibility, checkEligibility, createCommunity } = useMarketplaceShop()
const { t } = i18n.global

const step = ref(1)
const loading = ref(true)
const form = reactive({
  communityName: '', description: '', accessMode: 'INVITE_ONLY', discoverable: false, requireJoinApproval: true, membersCanInvite: false, chatEnabled: true, productAnnouncementsEnabled: true, campaignsEnabled: true,
  calendarEnabled: true, documentsEnabled: false, pollsEnabled: false, inviteExistingCustomers: false, autoInviteFutureCustomers: false, marketingOptInRequired: true, checkoutMode: 'CONTACT_SELLER', currency: 'EUR', timezone: 'Europe/Brussels', enableMarketDay: false, welcomeMessage: ''
})
const progress = computed(() => step.value * 20)
const stepLabel = computed(() => {
  const labels = [
    t('common.identity') || 'Identity',
    t('group.access') || 'Access',
    t('common.features') || 'Features',
    t('common.customers') || 'Customers',
    t('common.review') || 'Review'
  ]
  return labels[step.value - 1]
})

checkEligibility(props.shopId).finally(() => { loading.value = false })

async function submitCommunity() {
  try {
    const result = await createCommunity(props.shopId, form)
    f7.dialog.alert(t('shopCommunity.createdToast'), t('common.success') || 'Success')
    if (result?.groupId) {
      props.f7router.navigate(`/groups/${result.groupId}`)
    } else {
      props.f7router.navigate('/sell')
    }
  } catch (error) {
    f7.dialog.alert(error.message || t('shopCommunity.creationFailed'), t('common.error') || 'Error')
  }
}
</script>
